import { IntlShape } from 'react-intl';
import { getSharedLinks, infoFeatures, infoLinks } from './getSharedLinks';
import { infoNavigation, MenuItem } from './navigation';
import messages from '../lib/messages.json';
import { getTargetOrigin } from '../lib/redirectRouteToDomain';
import hasLocalizedStatisticsPage from '../lib/hasLocalizedStatisticsPage';
import getExecutiveMenuSegment from './getExecutiveMenuItem';
import { Newsboard } from '../lib/graphql/api/graphql';

type GetDrawerProperties = (
  intl: IntlShape,
  newsboard: Newsboard,
  locale?: string,
  hasNews?: boolean,
  hasJobsInLanding?: boolean,
  origin?: string,
) => MenuItem[];

const getDrawer: GetDrawerProperties = (
  intl, newsboard, locale, hasNews, hasJobsInLanding, origin,
) => {
  const links = newsboard === Newsboard.FASHIONUNITED_INFO && [
    ...infoFeatures,
    ...infoNavigation,
    ...infoLinks,
  ];
  const isUS = newsboard === Newsboard.FASHIONUNITED_COM;
  const hasSpecialPages = [
    Newsboard.FASHIONUNITED_BE,
    Newsboard.FASHIONUNITED_DE,
    Newsboard.FASHIONUNITED_ES,
    Newsboard.FASHIONUNITED_FR,
    Newsboard.FASHIONUNITED_IT,
    Newsboard.FASHIONUNITED_NL,
    Newsboard.FASHIONUNITED_UK,
  ].includes(newsboard);

  return links || [
    {
      id: 'news',
      items: [
        {
          id: 'news-feed',
          router: hasNews,
          route: `news-feed.${locale}`,
          href: 'https://fashionunited.com/just-in',
        },
        ...getSharedLinks(intl, hasNews),
        {
          id: 'category.news.background',
          href: intl.formatMessage(
            messages['category.news.background.href'],
          ),
          router: hasNews,
          route: 'category',
          params: {
            categoryPath: intl.formatMessage(
              messages['category.news.background.param'],
            ),
          },
        },
        {
          id: 'category.news.video',
          href: intl.formatMessage(
            messages['category.news.video.href'],
          ),
          router: !hasNews,
          route: 'category',
          params: {
            categoryPath: intl.formatMessage(
              messages['category.news.video.param'],
            ),
          },
        },
        {
          id: 'category.news.podcast',
          href: intl.formatMessage(
            messages['category.news.podcast.href'],
          ),
          router: !hasNews,
          route: 'category',
          params: {
            categoryPath: intl.formatMessage(
              messages['category.news.podcast.param'],
            ),
          },
        },
        {
          id: 'partnerContent',
          router: hasNews,
          href: intl.formatMessage(messages.partnerContentHref),
          route: 'partner-content',
        },
        {
          id: 'pressReleases',
          router: hasNews,
          href: intl.formatMessage(messages.pressReleasesHref),
          route: 'press-releases',
        },
      ],
    },
    ...getExecutiveMenuSegment(newsboard),
    {
      id: 'jobs',
      router: true,
      route: `jobs.${locale}`,
      items: [],
    },
    {
      id: 'postJob',
      href: 'https://fashionunited.com/company/submit-your-job-postings',
      router: isUS,
      route: 'submit-job-postings',
    },
    {
      id: 'discoverEmployers',
      href: 'https://fashionunited.com/jobs',
      router: hasJobsInLanding,
      route: 'company-profiles',
    },
    ...(isUS ? [
      {
        id: 'education',
        router: true,
        route: 'education',
        items: [],
      },
    ] : []),
    {
      id: 'marketplace',
      href: `${getTargetOrigin(origin)}/marketplace`,
      router: isUS,
      route: 'marketplace',
      items: [],
    },
    {
      id: 'brandDirectory',
      href: 'https://fashionunited.com/brands',
      router: isUS,
      route: 'brands-directory',
      items: [],
    },
    {
      id: 'fashionFinder',
      href: 'https://fashionunited.com/fashion-finder',
      router: isUS,
      route: 'fashion-finder',
      items: [],
    },
    {
      id: 'events',
      router: true,
      route: 'events',
      items: [],
    },
    {
      id: 'lookbook',
      href: `${getTargetOrigin(origin)}/lookbook`,
      router: isUS,
      route: 'lookbook',
      items: [],
    },
    {
      id: 'data',
      items: [
        {
          id: 'top100',
          href: 'https://fashionunited.com/i/top100',
          router: isUS,
          route: 'top100',
        },
        {
          id: 'statistics',
          router: true,
          route: hasLocalizedStatisticsPage(locale)
            ? `country-statistics.${locale}` : 'statistics',
        },
        ...(isUS || !hasSpecialPages) ? [{
          id: 'businessData',
          href: 'https://fashionunited.com/i',
          router: isUS,
          route: 'intelligence',
        }] : [],
        ...hasSpecialPages ? [{
          id: 'businessIntelligence',
          router: hasSpecialPages,
          route: 'specials',
          params: {
            slug: 'data',
          },
          href: '/specials/data',
        }] : [],
      ],
    },
    {
      id: 'trends',
      items: [],
      router: hasNews,
      route: 'trends',
      href: intl.formatMessage(
        messages['trends.href'],
      ),
    },
    ...hasSpecialPages ? [
      {
        id: 'specials.education',
        items: [],
        router: true,
        route: 'specials',
        params: {
          slug: intl.formatMessage(
            messages['specials.education.slug'],
          ),
        },
        href: intl.formatMessage(
          messages['specials.education.href'],
        ),
      },
    ] : [],
    {
      id: 'specials-directory',
      items: [],
      router: hasSpecialPages || isUS,
      route: 'specials-directory',
      href: intl.formatMessage(
        messages['specials-directory.href'],
      ),
    },
    {
      id: 'newsletter',
      items: [],
      href: intl.formatMessage(
        messages['newsletter.href'],
      ),
    },
    {
      id: 'searchTitle',
      items: [],
      href: intl.formatMessage(
        messages['search.href'],
      ),
      router: !hasNews,
      route: 'search',
    },
    {
      id: 'dashboard',
      items: [],
      href: 'https://dashboard.fashionunited.com',
      target: '_blank',
    },
  ];
};

export default getDrawer;
